import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth.ts'
import { requestPasswordReset } from '@/services/api/authClient.ts'
import { Modal } from '@/components/shared/Modal'

interface LoginModalProps {
  isOpen: boolean
  onClose: () => void
  onConnect: () => void
}

export const LoginModal: React.FC<LoginModalProps> = ({
  isOpen,
  onClose,
  onConnect,
}) => {
  const { t } = useTranslation('auth/login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const { login } = useAuth()
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [showForgot, setShowForgot] = useState(false)
  const [forgotEmail, setForgotEmail] = useState('')
  const [forgotPending, setForgotPending] = useState(false)
  const [forgotSuccess, setForgotSuccess] = useState(false)

  const validateEmail = (emailToValidate: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailToValidate)
  }

  const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!validateEmail(email)) {
      return
    }

    setError(null)
    setIsPending(true)

    try {
      await login({ email, password })
      setEmail('')
      setPassword('')
      onConnect()
    } catch (loginError) {
      setError(loginError instanceof Error ? loginError : new Error(t('error')))
    } finally {
      setIsPending(false)
    }
  }

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateEmail(forgotEmail)) return

    setForgotPending(true)
    setForgotSuccess(false)
    try {
      await requestPasswordReset(forgotEmail)
      setForgotSuccess(true)
    } catch (err) {
      // don't expose details to user
      console.error('Error occurred while requesting password reset:', err)
      setForgotSuccess(false)
    } finally {
      setForgotPending(false)
    }
  }

  const handleClose = () => {
    setShowForgot(false)
    setForgotEmail('')
    setForgotSuccess(false)
    setEmail('')
    setPassword('')
    setIsPending(false)
    setError(null)
    onClose()
  }

  const errorMessage = error
    ? t('error')
    : !validateEmail(email) && email
      ? t('invalidEmail')
      : ''

  return (
    <Modal isOpen={isOpen} onClose={handleClose} closeLabel={t('close')}>
      <div className="mb-6 flex flex-col items-center gap-4">
        <h2 className="text-xl font-semibold">{t('header1')}</h2>
        <img
          src="/img/cleverlink-logo-with-typo-color.svg"
          alt="Cleverlink Logo"
        />
      </div>
      {!showForgot ? (
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="email"
            required
            placeholder={t('emailPlaceholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring mb-6 focus:border-primary border-gray-300`}
          />
          <input
            type="password"
            required
            placeholder={t('passwordPlaceholder')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring focus:border-primary mb-2"
          />
          <div className="flex justify-end mb-4">
            <button
              type="button"
              className="text-xs text-primary hover:underline"
              onClick={() => {
                setShowForgot(true)
                setForgotEmail('')
                setForgotSuccess(false)
              }}
            >
              {t('forgotPassword')}
            </button>
          </div>
          <button
            type="submit"
            disabled={isPending}
            className="w-full py-2 rounded-xl bg-gradient-to-r from-pink-500 to-orange-500 text-white font-bold shadow-lg hover:from-pink-600 hover:to-orange-600 transition disabled:cursor-not-allowed disabled:opacity-60"
          >
            {t('cta')}
          </button>
          <div className="text-xs text-gray-400 text-center mt-2">
            {t('discoverText')}
          </div>
          <div className="text-xs text-center mt-2">
            {t('helpText')}{' '}
            <a
              href="mailto:contact@cleverlink.org"
              className="bg-gradient-to-r from-pink-500 to-orange-500 bg-clip-text text-transparent font-semibold hover:underline"
            >
              contact@cleverlink.org
            </a>
          </div>
          {errorMessage && (
            <div className="text-sm text-red-500 text-center mt-2">
              {errorMessage}
            </div>
          )}
        </form>
      ) : (
        <form onSubmit={handleForgotSubmit} className="space-y-4">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-1">
              {t('forgotPassword')}
            </h3>
            <p className="text-sm text-gray-500">{t('forgotDescription')}</p>
          </div>
          <input
            type="email"
            required
            placeholder={t('emailPlaceholder')}
            value={forgotEmail}
            onChange={(e) => setForgotEmail(e.target.value)}
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring mb-6 focus:border-primary border-gray-300`}
          />
          <div className="flex justify-between items-center gap-4">
            <button
              type="button"
              className="py-2 px-4 rounded-xl border"
              onClick={() => setShowForgot(false)}
            >
              {t('back')}
            </button>
            <button
              type="submit"
              disabled={forgotPending}
              className="py-2 px-4 rounded-xl bg-gradient-to-r from-pink-500 to-orange-500 text-white font-bold shadow-lg disabled:opacity-60"
            >
              {t('requestReset')}
            </button>
          </div>
          {forgotSuccess && (
            <div className="text-sm text-green-600 text-center mt-2">
              {t('resetRequested')}
            </div>
          )}
        </form>
      )}
    </Modal>
  )
}
